import { useCallback, useState, useTransition } from 'react';
import { useSuspenseQuery } from '@apollo/client/react';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { SearchXIcon, Trash2 } from 'lucide-react';
import { debounce } from 'lodash';

import { IntegrationMemberRoleEnum } from 'src/graphql/schema';
import { IntegrationMembersDocument } from 'src/graphql/actions/integration/integrationMembers.query';
import { withSuspense } from 'src/hoc';
import { EmptyBlock } from '@/components/ui/empty-block';
import { InputSearch } from '@/components/ui/input-search';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from 'src/lib/utils';

import type {
  IIntegrationMembersQuery,
  IIntegrationMembersQueryVariables,
} from 'src/graphql/actions/integration/integrationMembers.query';

import ShareAccessDialog from './ShareAccessDialog';
import RemoveMemberDialog from './RemoveMemberDialog';

type IIntegrationMember = IIntegrationMembersQuery['integrationMembers'][number];

const columnHelper = createColumnHelper<IIntegrationMember>();

const buildColumns = (canManage: boolean) => [
  columnHelper.accessor('user.name', {
    header: 'Name',
    cell: (props) => <span className="font-medium">{props.getValue()}</span>,
  }),
  columnHelper.display({
    header: ' ',
    cell: ({ row }) => {
      const { role, id } = row.original;

      if (role === IntegrationMemberRoleEnum.Owner) return <Badge>Owner</Badge>;
      if (canManage)
        return (
          <RemoveMemberDialog id={id}>
            <Button variant="ghost" size="icon" aria-label="Remove member" className="w-6 h-6">
              <Trash2 className="text-destructive" />
            </Button>
          </RemoveMemberDialog>
        );
      return null;
    },
    meta: {
      cellClassName: 'w-23 text-center',
      headerClassName: 'w-23',
    },
  }),
];

type Props = {
  integrationId: string;
  type: string;
  canManage?: boolean;
  autoOpenShare?: boolean;
  onShareDialogClose?: () => void;
};

const MembersTable = ({ integrationId, type, canManage = false, autoOpenShare = false, onShareDialogClose }: Props) => {
  const [query, setQuery] = useState('');
  const [isPending, startSearchTransition] = useTransition();

  const {
    data: { integrationMembers },
  } = useSuspenseQuery(IntegrationMembersDocument, {
    variables: { integrationId, ...(query && { query }) } as IIntegrationMembersQueryVariables,
  });

  const table = useReactTable({
    data: integrationMembers,
    columns: buildColumns(canManage),
    getCoreRowModel: getCoreRowModel(),
  });

  const debouncedOnSearch = useCallback(
    debounce((e) => {
      const searchValue = e.target.value;
      startSearchTransition(() => setQuery(searchValue));
    }, 500),
    [startSearchTransition],
  );

  const onClear = () => setQuery('');

  return (
    <div className="space-y-4">
      {type !== 'ShareFileIntegration' && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <InputSearch
            placeholder="Search users"
            onChange={debouncedOnSearch}
            onClear={onClear}
            isLoading={isPending}
            className="w-full sm:w-75 [&>input]:bg-white"
          />

          {canManage && (
            <div className="shrink-0">
              <ShareAccessDialog
                integrationId={integrationId}
                defaultOpen={autoOpenShare}
                onClose={onShareDialogClose}
              />
            </div>
          )}
        </div>
      )}

      <Card className="shadow-none overflow-hidden">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="bg-gray-50 text-xs text-muted-foreground font-bold whitespace-nowrap first:rounded-tl-md last:rounded-tr-md h-11"
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {integrationMembers.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={cn(
                    row.original.role === IntegrationMemberRoleEnum.Owner && 'bg-slate-100 hover:bg-slate-100',
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className={cell.column.columnDef.meta?.cellClassName}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={table.getAllColumns().length}>
                  <EmptyBlock
                    title="No results found"
                    description="There were no results for your search. Try another filter."
                    icon={SearchXIcon}
                    withCard={false}
                    classes={{
                      title: 'text-base',
                    }}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

export default withSuspense(MembersTable);
